import * as vp from 'vopay';
import * as Yup from 'yup';
import { useState, FunctionComponent, useEffect, JSX } from 'react';
import { Formik } from 'formik';
import moment from 'moment';
import styles from './TransferFund.module.scss';
import { useClientData } from '../../../providers/ClientData.provider';
import useFetchClientAccountBalance from '../../../hooks/useApi/useFetchClientAccountBalance';
import { TransferFundHandler, TransferFundRequest } from './types';
import { buildTransferFundRows } from './buildTransferFundRows.helper';
import TransferFundClientAccountSelect, { MultiSelectDropdownItemProps } from './TransferFundClientAccountSelect';
import TransferFundDialogueContext, { TransferFundCtx } from '../Dialogue/TransferFund/TransferFundContext';
import TransferFundDialogue from '../Dialogue/TransferFund/TransferFundDialogue';
import useFetchClientAccountList from '../../../hooks/useApi/useFetchClientAccountList';
import { useTranslation } from 'react-i18next';

const { Box, Button, Table, TextField, Select, Icon, DatepickerSimple, FilterableMultiSelector } = vp;

type TransferFundProps = {
  handlers: TransferFundHandler;
  defaultValues: TransferFundRequest;
  TransferFundContext: TransferFundCtx;
  isUnderReview: boolean;
  setIsUnderReview: (e: boolean) => void;
  setDebitorClientAccount: (debitorName: string) => void;
  setRecipientClientAccount: (debitorName: string) => void;
  setCountry: (country: string) => void;
};
type TransferFunds = {
  parent: {
    Amount: string | undefined;
    StartDate: string | undefined;
    TransferType: string | undefined;
    RecipientClientAccountIDSplit: string | undefined;
  };
};

type FC = FunctionComponent<TransferFundProps>;

const TransferFundForm: FC = (props: TransferFundProps) => {
  const {
    handlers,
    defaultValues,
    TransferFundContext,
    isUnderReview,
    setIsUnderReview,
    setDebitorClientAccount,
    setRecipientClientAccount,
    setCountry,
  } = props;
  const now = moment().format('YYYY-MM-DD');

  const [clientAccountListResponse, getClientAccountList] = useFetchClientAccountList({});
  const [debitorClientAccountListResponse, getDebitorClientAccountList] = useFetchClientAccountList({});
  const [recipientClientAccountListResponse, getRecipientClientAccountListResponse] = useFetchClientAccountList({});

  const [clientAccountBalanceResponse, getClientAccountBalance] = useFetchClientAccountBalance({});
  const [debitorDetail, setDebitorDetail] = useState<vp.ClientAccount>();
  const [recipientDetail, setRecipientDetail] = useState<vp.ClientAccount>();

  const [clientAccount, getClientAccount] = useState(defaultValues.DebitorClientAccountID || '');
  const [currency, setCurrency] = useState('CAD');

  const [shouldInitializeClientAccountList, setShouldInitializeClientAccountList] = useState(true);
  const [originalClientAccountList, setOriginalClientAccountList] = useState<object | undefined>(undefined);
  const [debitorClientAccountList, setDebitorClientAccountList] = useState({});
  const [recipientClientAccountList, setRecipientClientAccountList] = useState({});
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.TransferFunds' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const {
    response: { TransactionLabelOverrideEnabled, Country, USDEFTSendEnabled },
  } = useClientData();

  const transferFundRows = buildTransferFundRows(
    debitorClientAccountList,
    recipientClientAccountList,
    currency,
    TransactionLabelOverrideEnabled,
    Country,
    USDEFTSendEnabled,
    fetchDebitorListWithSearchTerm,
    setDebitorDetail,
    setRecipientDetail,
    debitorDetail,
    recipientDetail,
  );

  useEffect(() => {
    if (debitorDetail) {
      setDebitorClientAccount(debitorDetail.ClientName);
      setCountry(debitorDetail.Country);
    }
  }, [debitorDetail]);

  useEffect(() => {
    if (recipientDetail) {
      setRecipientClientAccount(recipientDetail.ClientName);
    }
  }, [recipientDetail]);

  useEffect(() => {
    getClientAccountList({
      ClientType: '',
      Limit: 100,
    });
    setShouldInitializeClientAccountList(true);
  }, []);

  useEffect(() => {
    if (shouldInitializeClientAccountList && clientAccountListResponse.Success) {
      setOriginalClientAccountList(clientAccountListResponse.ClientAccounts);
      setDebitorClientAccountList(clientAccountListResponse.ClientAccounts);
      setRecipientClientAccountList(clientAccountListResponse.ClientAccounts);
      setShouldInitializeClientAccountList(false);
    }
  }, [clientAccountListResponse]);
  function fetchDebitorListWithSearchTerm(searchTerm: string | undefined) {
    if ((searchTerm === '' || searchTerm === undefined) && originalClientAccountList) {
      setDebitorClientAccountList(originalClientAccountList);
    } else {
      getDebitorClientAccountList({
        ClientType: '',
        Limit: 100,
        SearchText: searchTerm === '' ? undefined : searchTerm,
      });
    }
  }

  function fetchRecipientListWithSearchTerm(searchTerm: string | undefined) {
    if ((searchTerm === '' || searchTerm === undefined) && originalClientAccountList) {
      setRecipientClientAccountList(originalClientAccountList);
    } else {
      getRecipientClientAccountListResponse({
        ClientType: '',
        Limit: 100,
        SearchText: searchTerm === '' ? undefined : searchTerm,
      });
    }
  }

  useEffect(() => {
    if (debitorClientAccountListResponse.Success) {
      setDebitorClientAccountList(debitorClientAccountListResponse.ClientAccounts);
    }
  }, [debitorClientAccountListResponse]);

  useEffect(() => {
    if (recipientClientAccountListResponse.Success) {
      setRecipientClientAccountList(recipientClientAccountListResponse.ClientAccounts);
    }
  }, [recipientClientAccountListResponse]);

  const [canSubmit, setCanSubmit] = useState(false);
  const availableBalance = clientAccountBalanceResponse.AvailableFunds;
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Initialize timeoutRef variable

    // Function to send the request
    const sendRequest = () => {
      if (clientAccount !== '') {
        getClientAccountBalance({
          ClientAccountID: clientAccount,
          Currency: currency,
        });
      }
    };

    // Set a new timeout to send the request after 100 milliseconds
    const timeoutRef = setTimeout(sendRequest, 100);

    // Cleanup function to clear the timeout on unmount or when dependencies change
    return () => {
      clearTimeout(timeoutRef);
    };
  }, [clientAccount, currency]);

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.handleSubmit}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setTouched,
        setFieldTouched,
        setValues,
      }) => {
        useEffect(() => {
          if (values.RecipientClientAccountID !== '') {
            setTouched({ ...touched, RecipientClientAccountID: true });
          }
        }, [values.RecipientClientAccountID]);

        useEffect(() => {
          if (values.Currency !== '') {
            setCurrency(values?.Currency || 'CAD');
          }
        }, [values.Currency]);

        useEffect(() => {
          if (Object.keys(errors).length > 0) {
            setCanSubmit(false);
          } else {
            setCanSubmit(true);
          }
        }, [errors]);

        useEffect(() => {
          if (values.DebitorClientAccountID !== '') {
            setValues({ ...values, ClientName: values.DebitorClientAccountID });
          }
        }, [values.DebitorClientAccountID]);

        const transferFundTable: vp.TableProps = {
          name: 'transfer-fund-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: transferFundRows
            .filter((x) => x.visible(values))
            .map((mapItem, index) => {
              const subElement = mapItem.getSubElement ? mapItem.getSubElement(values) : undefined;
              return {
                Field: (
                  <Box ai="center">
                    {mapItem.label} {getRequiredIcon(mapItem.require)}
                  </Box>
                ),
                Input: (
                  <Box flx="col">
                    {mapItem.type === 'singleDate' && (
                      <div className={styles.datePickerContainer}>
                        <DatepickerSimple
                          name={mapItem.id}
                          value={String(values[mapItem.id])}
                          error={errors[mapItem.id] && String(values[mapItem.id]) !== '' ? errors[mapItem.id] : null}
                          onBlur={handleBlur}
                          onChange={(e: { target: { value: string } }) => {
                            if (e.target && e.target.value === 'undefined') {
                              e.target.value = String(values[mapItem.id]);
                            }
                            handleChange(e);
                          }}
                          isDisabled={!!isUnderReview}
                        />
                      </div>
                    )}

                    {mapItem.type === 'input' && (
                      <TextField
                        preset={mapItem.preset}
                        key={mapItem.id}
                        name={mapItem.id}
                        variant="standard"
                        value={String(values[mapItem.id])}
                        placeholder={mapItem.placeholder}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        maxLength={mapItem.maxLength}
                        error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                        disabled={!!isUnderReview}
                      />
                    )}

                    {mapItem.type === 'multiSelector' && (
                      <div>
                        {mapItem.id === 'RecipientClientAccountID' &&
                        Object.values(recipientClientAccountListResponse.ClientAccounts) ? (
                          <div className={styles.seletorContainer}>
                            <FilterableMultiSelector
                              name={mapItem.id}
                              value={
                                mapItem.getMultiSelectValues
                                  ? mapItem.getMultiSelectValues(String(values[mapItem.id]))
                                  : []
                              }
                              variant="underline"
                              items={mapItem.getMultiSelectOptions ? mapItem.getMultiSelectOptions(searchTerm) : []}
                              renderOption={mapItem.renderMultiselectLabel}
                              labelSize="16px"
                              indicatorMinHeight="40px"
                              height="42px"
                              minHeight="42px"
                              isNew={false}
                              setFieldValue={setFieldValue}
                              error={
                                errors.RecipientClientAccountID && touched.RecipientClientAccountID
                                  ? errors.RecipientClientAccountID
                                  : null
                              }
                              // do not sort long lists take for ever to render.
                              nosort
                              searchCallback={(_searchTeam) => {
                                fetchRecipientListWithSearchTerm(_searchTeam === '' ? undefined : _searchTeam);
                                setSearchTerm(_searchTeam);
                              }}
                              disabled={!!isUnderReview}
                              onSelect={(items) => {
                                if (mapItem.onMultiselect) {
                                  mapItem.onMultiselect(items);
                                }
                              }}
                            />
                          </div>
                        ) : (
                          <Select
                            options={
                              mapItem.getSelectOptions ? mapItem.getSelectOptions() : mapItem.customOptions || []
                            }
                            renderOption={mapItem.renderOption}
                            variant="standard"
                            key={mapItem.id}
                            name={mapItem.id}
                            value={String(values[mapItem.id])}
                            placeholder={t('Form.defaultSelectPlaceholder')}
                            noOrdering
                            enablePagination
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                            disabled={!!isUnderReview}
                            disableFiltering={!!isUnderReview}
                          />
                        )}
                      </div>
                    )}

                    {mapItem.type === 'multiSelectDropdown' && (
                      <TransferFundClientAccountSelect
                        totalAmount={String(values.Amount)}
                        variant="standard"
                        options={mapItem.getSelectOptions ? mapItem.getSelectOptions() : {}}
                        key={mapItem.id}
                        name={mapItem.id}
                        value={String(values.RecipientClientAccountID)}
                        dropDownValues={String(values.RecipientClientAccountIDSplit)}
                        placeholder={mapItem.placeholder}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        transferType={String(values.TransferType)}
                        error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                        identifier={`${mapItem.id}-${String(index)}`}
                        setFieldValue={() => {}}
                        currency={currency}
                        splitError={
                          errors.RecipientClientAccountIDSplit && values.RecipientClientAccountID.split('|').length > 1
                            ? errors.RecipientClientAccountIDSplit
                            : null
                        }
                        disabled={!!isUnderReview}
                      />
                    )}

                    {mapItem.id === 'DebitorClientAccountID' && (
                      <Select
                        options={mapItem.getSelectOptions ? mapItem.getSelectOptions() : mapItem.customOptions || []}
                        renderOption={mapItem.renderOption}
                        variant="standard"
                        key={mapItem.id}
                        name={mapItem.id}
                        value={String(values[mapItem.id])}
                        placeholder={mapItem.placeholder}
                        noOrdering
                        onBlur={handleBlur}
                        enablePagination
                        labelOverride={debitorDetail?.ClientName || undefined}
                        onChange={(e) => {
                          handleChange(e);
                          getClientAccount(e.target.value);
                          if (mapItem.onChangeCallBack) {
                            mapItem.onChangeCallBack(e, values, setFieldValue, setFieldTouched);
                          }
                        }}
                        error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                        disabled={!!isUnderReview}
                        disableFiltering={mapItem.disableFiltering ? mapItem.disableFiltering() : false}
                        searchCallback={mapItem.onSearch}
                      />
                    )}
                    {mapItem.type !== 'singleDate' &&
                      mapItem.type !== 'input' &&
                      mapItem.type !== 'multiSelector' &&
                      mapItem.type !== 'multiSelectDropdown' &&
                      mapItem.id !== 'DebitorClientAccountID' &&
                      mapItem.id === 'RecipientClientAccountID' && (
                        <Select
                          options={mapItem.getSelectOptions ? mapItem.getSelectOptions() : mapItem.customOptions || []}
                          renderOption={mapItem.renderOption}
                          variant="standard"
                          key={mapItem.id}
                          name={mapItem.id}
                          value={String(values[mapItem.id])}
                          placeholder={mapItem.placeholder}
                          labelOverride={recipientDetail?.ClientName || undefined}
                          noOrdering
                          enablePagination
                          onBlur={handleBlur}
                          onChange={(e) => {
                            handleChange(e);
                            if (mapItem.onChangeCallBack) {
                              mapItem.onChangeCallBack(e, values, setFieldValue, setFieldTouched);
                            }
                          }}
                          error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                          disabled={!!isUnderReview}
                          disableFiltering={mapItem.disableFiltering ? mapItem.disableFiltering() : false}
                          searchCallback={(_searchTeam) => {
                            fetchRecipientListWithSearchTerm(_searchTeam === '' ? undefined : _searchTeam);
                            setSearchTerm(_searchTeam);
                          }}
                        />
                      )}
                    {mapItem.type !== 'singleDate' &&
                      mapItem.type !== 'input' &&
                      mapItem.type !== 'multiSelector' &&
                      mapItem.type !== 'multiSelectDropdown' &&
                      mapItem.id !== 'DebitorClientAccountID' &&
                      mapItem.id !== 'RecipientClientAccountID' && (
                        <Select
                          options={mapItem.getSelectOptions ? mapItem.getSelectOptions() : mapItem.customOptions || []}
                          renderOption={mapItem.renderOption}
                          variant="standard"
                          key={mapItem.id}
                          name={mapItem.id}
                          value={String(values[mapItem.id])}
                          placeholder={mapItem.placeholder}
                          noOrdering
                          enablePagination
                          onBlur={handleBlur}
                          onChange={(e) => {
                            handleChange(e);
                            if (mapItem.onChangeCallBack) {
                              mapItem.onChangeCallBack(e, values, setFieldValue, setFieldTouched);
                            }
                          }}
                          error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                          disabled={!!isUnderReview}
                          disableFiltering={mapItem.disableFiltering ? mapItem.disableFiltering() : false}
                          searchCallback={(_searchTeam) => {
                            mapItem.id === 'RecipientClientAccountID' &&
                              fetchRecipientListWithSearchTerm(_searchTeam === '' ? undefined : _searchTeam);
                            setSearchTerm(_searchTeam);
                          }}
                        />
                      )}
                    {subElement}
                  </Box>
                ),
              };
            }),
        };

        return (
          <>
            <Table {...transferFundTable} />
            <TransferFundDialogueContext.Provider value={TransferFundContext}>
              <form onSubmit={handleSubmit}>
                <Box mt="md" jc="fe" gap="sm">
                  {isUnderReview ? (
                    <Button
                      id="Fund-back"
                      color="secondary"
                      label={tGlobal('Buttons.back')}
                      onClick={() => setIsUnderReview(false)}
                    />
                  ) : null}
                  <Button
                    isDisabled={!canSubmit}
                    id="transfer-fund-submit"
                    label={isUnderReview ? t('Form.transferButton') : t('Form.reviewButton')}
                    type="submit"
                  />
                </Box>
              </form>
              <TransferFundDialogue handleDialogue={handlers.handleTransferFundDialogue} />
            </TransferFundDialogueContext.Provider>
          </>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      Currency: Yup.string().required(),
      TransferType: Yup.string().required(),
      DebitorClientAccountID: Yup.string()
        .when(['Amount', 'TransferType'], {
          is: (Amount: string, TransferType: string) =>
            parseFloat(Amount) > 0.0 && (TransferType === 'Transfer' || TransferType === 'TransferAndWithdraw'),
          then: Yup.string().test(
            'debitor-available-fund-validation',
            t('Form.Validation.insufficientFunds', { availableBalance }),
            (_value, obj) => {
              const availableFundsValidation = Number(obj.parent.Amount) > Number(availableBalance);
              return !availableFundsValidation;
            },
          ),
        })
        .required(),
      RecipientClientAccountID: Yup.string()
        .test('same-account-validation', t('Form.Validation.invalidAccount'), (value, fundTransfer) => {
          let debitAccount = '';
          const isObjectWithDebitorAccountID = (obj: unknown): obj is { DebitorClientAccountID: string } =>
            typeof obj === 'object' && obj !== null && 'DebitorClientAccountID' in obj;

          if (isObjectWithDebitorAccountID(fundTransfer.parent)) {
            if (fundTransfer.parent.DebitorClientAccountID) {
              debitAccount = fundTransfer.parent.DebitorClientAccountID;
            }
          }

          const sameAccountValidation = (value !== '' && value?.split('|').includes(debitAccount)) || false;
          return !sameAccountValidation;
        })
        .required(),
      RecipientClientAccountIDSplit: Yup.string()
        .test('recipient-given-validation', t('Form.Validation.clientAccountMustBeSpecified'), (value: any) => {
          if (!value) return true;

          const items: MultiSelectDropdownItemProps[] = JSON.parse(value);
          return !items.map((item) => item.ClientAccountID === '').includes(true);
        })
        .test('recipient-more-than-one-validation', t('Form.Validation.moreThanOneRecipient'), (value: any) => {
          if (!value) return true;
          return JSON.parse(value).length != 1;
        })
        .test(
          'recipient-amount-valid-validation',
          t('Form.Validation.accumulatedAmount'),
          (value: any, TransferFunds: TransferFunds) => {
            if (!value || !TransferFunds.parent.Amount) return true;

            const amountTotal = JSON.parse(value).reduce((accumulator: number, item: MultiSelectDropdownItemProps) => {
              if (item.Amount) {
                accumulator += parseFloat(item.Amount);
              }
              return accumulator;
            }, 0);

            return amountTotal.toFixed(2) <= parseFloat(TransferFunds.parent.Amount).toFixed(2);
          },
        )
        .test(
          'recipient-percent-valid-validation',
          t('Form.Validation.accumulatedPercent'),
          (value: any, fundTransfer) => {
            if (!value) return true;

            const recipients = fundTransfer.parent.RecipientClientAccountID?.split('|') || [];
            if (recipients.length === 1 && fundTransfer.parent.RecipientClientAccountIDSplit !== '') {
              return true;
            }

            const percentageTotal = JSON.parse(value).reduce(
              (accumulator: number, item: MultiSelectDropdownItemProps) => {
                if (item.Percentage) {
                  accumulator += parseFloat(item.Percentage);
                }
                return accumulator;
              },
              0,
            );

            return percentageTotal <= 100;
          },
        )
        .test(
          'recipient-amount-not-zero-validation',
          t('Form.Validation.cannotTransferAmountOfZero'),
          (value: any, fundTransfer) => {
            if (!value) return true;

            const recipients = fundTransfer.parent.RecipientClientAccountID?.split('|') || [];
            if (recipients.length === 1 && fundTransfer.parent.RecipientClientAccountIDSplit !== '') {
              return true;
            }

            let results = true;

            JSON.parse(value).forEach((item: MultiSelectDropdownItemProps) => {
              if (!item.Amount || item.Amount == '0') {
                results = false;
              }
            });

            const test = JSON.parse(value).some((item: MultiSelectDropdownItemProps) => item.Percentage)
              ? true
              : results;

            return test;
          },
        )
        .test(
          'recipient-percent-not-zero-validation',
          t('Form.Validation.eachPercentageMustBeAboveZero'),
          (value: any, fundTransfer) => {
            if (!value) return true;

            const recipients = fundTransfer.parent.RecipientClientAccountID?.split('|') || [];
            if (recipients.length === 1 && fundTransfer.parent.RecipientClientAccountIDSplit !== '') {
              return true;
            }

            let results = true;

            JSON.parse(value).forEach((item: MultiSelectDropdownItemProps) => {
              if (!item.Percentage || item.Percentage == '0') results = false;
            });

            return JSON.parse(value).some((item: MultiSelectDropdownItemProps) => item.Amount) ? true : results;
          },
        )
        .test(
          'recipient-no-remainder-validation',
          t('Form.Validation.recipientNoRemainder'),
          (value: any, TransferFunds) => {
            if (!value || !TransferFunds.parent.TransferType || !TransferFunds.parent.Amount) return true;

            if (TransferFunds.parent.RecipientClientAccountIDSplit !== '') {
              const RecipientClientAccountIDSplit = TransferFunds.parent?.RecipientClientAccountIDSplit || '';

              if (RecipientClientAccountIDSplit === '') return true;
              const recipients = TransferFunds.parent.RecipientClientAccountID.split('|') || [];
              if (recipients.length === 1 && TransferFunds.parent.RecipientClientAccountIDSplit !== '') {
                return true;
              }
            }

            const transferTypes = ['Transfer', 'TransferAndWithdraw'];

            if (!transferTypes.includes(TransferFunds.parent.TransferType)) return true;

            const { percentTotal, amountTotal } = JSON.parse(value).reduce(
              (accumulator: any, item: MultiSelectDropdownItemProps) => {
                if (item.Amount) {
                  accumulator.amountTotal += parseFloat(item.Amount);
                }
                if (item.Percentage) {
                  accumulator.percentTotal += parseFloat(item.Percentage);
                }
                return accumulator;
              },
              { amountTotal: 0, percentTotal: 0 },
            );
            return amountTotal == TransferFunds.parent.Amount || percentTotal != 0;
          },
        )
        .test(
          'recipient-no-remainder-validation',
          t('Form.Validation.transferTypeNotAllowRemainingFunds'),
          (value: any, TransferFunds) => {
            if (!value || !TransferFunds.parent.TransferType || !TransferFunds.parent.Amount) return true;

            if (TransferFunds.parent.RecipientClientAccountIDSplit !== '') {
              const RecipientClientAccountIDSplit = TransferFunds.parent?.RecipientClientAccountIDSplit || '';

              if (RecipientClientAccountIDSplit === '') return true;
              const recipients = TransferFunds.parent.RecipientClientAccountID.split('|') || [];
              if (recipients.length === 1 && TransferFunds.parent.RecipientClientAccountIDSplit !== '') {
                return true;
              }
            }

            const transferTypes = ['Transfer', 'TransferAndWithdraw'];

            if (!transferTypes.includes(TransferFunds.parent.TransferType)) return true;

            const { percentTotal, amountTotal } = JSON.parse(value).reduce(
              (accumulator: any, item: MultiSelectDropdownItemProps) => {
                if (item.Amount) {
                  accumulator.amountTotal += parseFloat(item.Amount);
                }
                if (item.Percentage) {
                  accumulator.percentTotal += parseFloat(item.Percentage);
                }
                return accumulator;
              },
              { amountTotal: 0, percentTotal: 0 },
            );
            return percentTotal == 100 || amountTotal != 0;
          },
        )
        .when(['RecipientClientAccountID'], {
          is: (RecipientClientAccountID: string) => RecipientClientAccountID?.split('|').length > 1,
          then: Yup.string()
            .test(
              'RecipientClientAccountID-split-method-validation',
              t('Form.Validation.transferNotAllowRemainingFunds'),
              (value: any, TransferFunds: TransferFunds) => {
                if (!value || !TransferFunds.parent.TransferType) return true;

                const transferTypes = ['TransferAndWithdraw'];

                if (!transferTypes.includes(TransferFunds.parent.TransferType)) return true;

                const usingAmount = JSON.parse(value).some(
                  (item: MultiSelectDropdownItemProps) => item.Amount && item.Amount !== '',
                );

                const percentageTotal = JSON.parse(value).reduce(
                  (accumulator: number, item: MultiSelectDropdownItemProps) => {
                    if (item.Percentage) {
                      accumulator += parseFloat(item.Percentage);
                    }
                    return accumulator;
                  },
                  0,
                );
                return usingAmount || percentageTotal >= 100;
              },
            )
            .required(),
          otherwise: Yup.string(),
        }),
      Amount: Yup.string()
        .test('amount-validation', t('Form.Validation.amountShouldBeGreater'), (value) => {
          const amountValidation = Number(value) <= 0.0;
          return !amountValidation;
        })
        .required(),
      TransactionLabel: Yup.string().max(30, t('Form.Validation.valueSuppliedExceedsMaximumLength')),
      ScheduleType: Yup.string().when(['PaymentMethod'], {
        is: (TransferType: string) => TransferType !== 'Transfer',
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      TakeFromAccountOn: Yup.string().when(['PaymentMethod', 'ScheduleType'], {
        is: (TransferType: string, ScheduleType: string) => TransferType !== 'Transfer' && ScheduleType === 'single',
        then: Yup.string()
          .test(
            'start-date-validation',
            t('Form.Validation.invalidDateTakeFromAccountOn', { date: moment(now).format('YYYY-MM-DD') }),
            (value: any) => {
              const dateValidation = value !== 'underfined' && moment(Number(value)).format('YYYY-MM-DD') < now;
              return !dateValidation;
            },
          )
          .required(),
        otherwise: Yup.string(),
      }),
      StartDate: Yup.string().when(['PaymentMethod', 'ScheduleType'], {
        is: (TransferType: string, ScheduleType: string) => TransferType !== 'Transfer' && ScheduleType === 'recurring',
        then: Yup.string()
          .test(
            'start-date-validation',
            t('Form.Validation.invalidDateStartDate', { date: moment(now).add(1, 'days').format('YYYY-MM-DD') }),
            (value: string | undefined) => {
              const dateValidation =
                value !== 'underfined' &&
                moment(Number(value)).format('YYYY-MM-DD') < moment(now).add(1, 'days').format('YYYY-MM-DD');
              return !dateValidation;
            },
          )
          .required(),
        otherwise: Yup.string(),
      }),
      Frequency: Yup.string().when(['PaymentMethod', 'ScheduleType'], {
        is: (TransferType: string, ScheduleType: string) => TransferType !== 'Transfer' && ScheduleType === 'recurring',
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      SemiMonthlyDates: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Frequency'], {
        is: (TransferType: string, ScheduleType: string, Frequency: string) =>
          TransferType !== 'Transfer' && ScheduleType === 'recurring' && Frequency === 'semi-monthly',
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      Ending: Yup.string().when(['PaymentMethod', 'ScheduleType'], {
        is: (TransferType: string, ScheduleType: string) => TransferType !== 'Transfer' && ScheduleType === 'recurring',
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      EndDate: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Ending'], {
        is: (TransferType: string, ScheduleType: string, Ending: string) =>
          TransferType !== 'Transfer' && ScheduleType === 'recurring' && Ending === 'End Date',
        then: Yup.string()
          .test(
            'end-date-validation',
            t('Form.Validation.invalidDateEndDate'),
            (value: string | undefined, TransferFunds: TransferFunds) => {
              const endDate = new Date(moment(Number(value)).format('YYYY-MM-DD'));
              const startDate = new Date(moment(Number(TransferFunds.parent.StartDate)).format('YYYY-MM-DD'));
              const dateValidation = endDate === startDate || endDate < startDate;
              return !dateValidation;
            },
          )
          .required(),
        otherwise: Yup.string(),
      }),
      EndingAfterPayments: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Ending'], {
        is: (TransferType: string, ScheduleType: string, Ending: string) =>
          TransferType !== 'Transfer' && ScheduleType === 'recurring' && Ending === 'Payments',
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      Description: Yup.string().when(['PaymentMethod', 'ScheduleType', 'TakeFromAccountOn'], {
        is: (TransferType: string, ScheduleType: string, TakeFromAccountOn: string) =>
          TransferType !== 'Transfer' &&
          (ScheduleType === 'recurring' ||
            (TakeFromAccountOn && !vp.IsTodayDate(moment(Number(TakeFromAccountOn)).format('YYYY-MM-DD')))),
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
    });
  }

  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default TransferFundForm;
